import { Context } from "../../../../../models/context";
import { LocalizationService } from "../../../../../services/localization-service";
import { Util } from "../../../../../utils/util";
import { CustomizeItemSectionHeader } from "./customize-item-section-header";
import { ICustomizeItemInstruction } from "../../customize-item.interface";
import { IDataInstruction } from "../../../../../models/data.interface";
import { IName } from "../../../../../interfaces/global.interfaces";

interface ICustomizeItemSpecialInstructionTemp {
    id: number,
    name: IName[],
    isMultiSelect: boolean,
    instructions: IDataInstruction[]
}

/**
 * Manages the rendering and state of the "Special Instructions" section
 * in the item customization UI.
 */
export class CustomizeItemSpecialInstructions {
    private readonly _context: Context;
    private readonly _localizationService: LocalizationService;
    private readonly _containerElement: HTMLElement | null;
    private _selectedInstructions: ICustomizeItemInstruction[];

    /**
     * @param {Context} context - Application context (e.g., store, language).
     * @param {LocalizationService} localizationService - Service for retrieving localized strings.
     * @param {IDataInstruction[]} instructions - The full list of instructions associated with the menu item.
     * @param {number} webCategoryId - The web category ID of the menu item.
     * @param {number} sizeId - The size ID of the menu item.
     * @param {ICustomizeItemInstruction[]} selectedInstructions - List of initially selected instructions.
     */
    constructor(context: Context, localizationService: LocalizationService, instructions: IDataInstruction[], webCategoryId: number, sizeId: number, selectedInstructions: ICustomizeItemInstruction[]) {
        this._context = context;
        this._localizationService = localizationService;
        this._selectedInstructions = selectedInstructions;

        this._containerElement = this._generateSpecialInstructionsSection(instructions, webCategoryId, sizeId);
    }

    /**
     * Gets the container DOM element representing the special instructions section.
     * @returns {HTMLElement | null} The rendered section element, or null if no instructions apply.
     */
    public get element(): HTMLElement | null {
        return this._containerElement;
    }

    /**
     * Gets the current list of selected special instructions.
     * @returns {ICustomizeItemInstruction[]} The selected instructions.
     */
    public get instructions(): ICustomizeItemInstruction[] {
        return this._selectedInstructions;
    }

    /**
     * Generates and returns the DOM section for special instructions.
     * Groups instructions by their IDs and filters based on item context.
     *
     * @param {IDataInstruction[]} instructions - The full instruction list.
     * @param {number} webCategoryId - Web category for the menu item.
     * @param {number} sizeId - Size ID for filtering relevant instructions.
     * @returns {HTMLElement | null} The rendered instruction section or null if no instructions shown.
     */
    private _generateSpecialInstructionsSection(instructions: IDataInstruction[], webCategoryId: number, sizeId: number): HTMLElement | null {
        if (instructions.length === 0)
            return null;

        const parentDiv = document.createElement("div");

        // Group instructions by shared specialInstructionsId
        const specialInstructions = this._groupInstructionsBySpecialInstructionId(instructions);

        // Create and append the header
        const header = new CustomizeItemSectionHeader(this._localizationService, this._localizationService.names("SpecialInstructions"), false).element;
        parentDiv.appendChild(header);

        // Create a wrapper for the instructions body
        const body = document.createElement("div");
        body.classList.add("customize-item-modifier-wrapper");
        parentDiv.appendChild(body);

        // Sort special instructions by ID, with ID 0 at the end
        this._sortSpecialInstructions(specialInstructions);

        // Generate and append special instruction groups
        let specialInstructionsShown = false;
        for (const specialInstruction of specialInstructions) {
            const isRelevant = specialInstruction.instructions.some(instruction =>
                instruction.Items.some(x => x.ItemWebCategoryId === webCategoryId && x.SizeId === sizeId)
            );
            if (!isRelevant)
                continue;

            specialInstructionsShown = true;
            const instructionGroup = this._generateInstructionGroup(specialInstruction, webCategoryId, sizeId);
            body.appendChild(instructionGroup);
        }

        return specialInstructionsShown ? parentDiv : null;
    }

    /**
     * Groups instructions by their `SpecialInstructionsId` into logical sets.
     * Each group may contain one or more instructions that share the same ID.
     *
     * @param {IDataInstruction[]} instructions - The instructions to group.
     * @returns {ICustomizeItemSpecialInstructionTemp[]} Grouped instruction sets.
     */
    private _groupInstructionsBySpecialInstructionId(instructions: IDataInstruction[]): ICustomizeItemSpecialInstructionTemp[] {
        const groupedInstructions: ICustomizeItemSpecialInstructionTemp[] = [];

        for (const instruction of instructions) {
            const group = groupedInstructions.find(x => x.id === instruction.SpecialInstructionsId);
            if (group) {
                group.instructions.push(instruction);
            } else {
                groupedInstructions.push({
                    id: instruction.SpecialInstructionsId,
                    name: instruction.SpecialInstructionsNames,
                    isMultiSelect: instruction.SpecialInstructionsId === 0 ? true : instruction.IsMultiSelect,
                    instructions: [instruction],
                });
            }
        }

        return groupedInstructions;
    }

    /**
     * Sorts the special instruction groups in ascending order by ID,
     * placing the group with ID 0 (e.g., "Other") at the end.
     *
     * @param {ICustomizeItemSpecialInstructionTemp[]} specialInstructions - The list of instruction groups to sort.
     */
    private _sortSpecialInstructions(specialInstructions: ICustomizeItemSpecialInstructionTemp[]): void {
        specialInstructions.sort((a, b) => {
            if (a.id === 0) return 1;
            if (b.id === 0) return -1;
            return a.id - b.id;
        });
    }

    /**
     * Builds the HTML element for a special instruction group, including its header and all valid instruction options.
     *
     * @param {ICustomizeItemSpecialInstructionTemp} specialInstruction - The group of instructions to render.
     * @param {number} webCategoryId - The web category of the menu item (used for filtering relevant instructions).
     * @param {number} sizeId - The size ID of the menu item (used for filtering relevant instructions).
     * @returns {HTMLElement} The container element for the rendered instruction group.
     */
    private _generateInstructionGroup(
        specialInstruction: ICustomizeItemSpecialInstructionTemp,
        webCategoryId: number,
        sizeId: number
    ): HTMLElement {
        const groupDiv = document.createElement("div");
        groupDiv.classList.add("customize-item-instruction");
        groupDiv.setAttribute("aria-labelledby", `h3_customize_item_special_instruction_${specialInstruction.id}`);
        groupDiv.setAttribute("role", specialInstruction.isMultiSelect ? "group" : "listbox");

        // Create group header
        const headerTitle = specialInstruction.id === 0 ? this._localizationService.names("Other") : this._localizationService.GetName(specialInstruction.name);
        const chooseText = specialInstruction.isMultiSelect ? this._localizationService.names("ChooseMany") : this._localizationService.names("ChooseOne");
        const header = `
            <div class="customize-item-instruction-group-header">
                <h3 id="h3_customize_item_special_instruction_${specialInstruction.id}">${headerTitle}</h3>
                <span>${chooseText}</span>
            </div>`;
        groupDiv.appendChild(Util.createHtmlElementFromTemplate(header));

        // Generate and append instruction options
        const instructionContainer = document.createElement("div");
        instructionContainer.classList.add("customize-item-instruction-group");

        for (const instruction of specialInstruction.instructions) {
            const isValidInstruction = instruction.Items.some(x => x.ItemWebCategoryId === webCategoryId && x.SizeId === sizeId);
            if (!isValidInstruction)
                continue;

            const name = this._localizationService.GetNewName(instruction.Names);
            const isSelected = this._selectedInstructions.some(x => x.specialInstructionId === specialInstruction.id && x.commentId === instruction.CommentId);
            const option = this._createInstructionOption(specialInstruction.id, instruction.CommentId, name, specialInstruction.isMultiSelect, isSelected);
            instructionContainer.appendChild(option);
        }

        groupDiv.appendChild(instructionContainer);
        return groupDiv;
    }

    /**
     * Creates a label and input element for a single instruction option (radio or checkbox).
     * Clicks on the label trigger selection logic via `_handleInstructionClick`.
     *
     * @param {number} specialInstructionId - The ID of the instruction group this option belongs to.
     * @param {number} instructionId - The specific comment/instruction ID.
     * @param {string} name - The display name for the instruction.
     * @param {boolean} isMultiSelect - Whether the instruction allows multiple selections (checkbox) or not (radio).
     * @param {boolean} [isSelected] - Whether the instruction is initially selected.
     * @returns {HTMLElement} The complete instruction option element.
     */
    private _createInstructionOption(specialInstructionId: number, instructionId: number, name: string, isMultiSelect: boolean, isSelected?: boolean): HTMLElement {
        const inputId = `inp_customize_item_instruction_${specialInstructionId}_${instructionId}`;
        const inputName = `inp_customize_item_instruction_${specialInstructionId}`;

        const ariaStateAttr = isMultiSelect
            ? `aria-checked="${isSelected ? 'true' : 'false'}"`
            : `role="option" aria-selected="${isSelected ? 'true' : 'false'}"`;

        const html = `
        <label id="lbl_customize_item_instruction_${specialInstructionId}_${instructionId}" class="option-row">
            <span>${name}</span>
            <div class="custom-radio">
                <input type="checkbox" 
                       id="${inputId}" 
                       name="${inputName}"
                       ${ariaStateAttr}
                       ${isSelected ? "checked" : ""}>
                <span class="checkmark"></span>
            </div>
        </label>`;

        const div = Util.createHtmlElementFromTemplate(html);

        const checkbox = div.querySelector(`#inp_customize_item_instruction_${specialInstructionId}_${instructionId}`);

        checkbox?.addEventListener("change", (event: Event) => {
            const target = event.target as HTMLInputElement;
            this._handleInstructionClick(target.checked, specialInstructionId, instructionId, isMultiSelect);
        });

        return div;
    }

    private readonly _handleInstructionClick = (checked: boolean, specialInstructionId: number, instructionId: number, isMultiSelect: boolean): void => {
        if (isMultiSelect) {
            this._handleMultiSelectInstructionClick(checked, specialInstructionId, instructionId);
            return;
        }
        this._handleSingleSelectInstructionClick(checked, specialInstructionId, instructionId);
    }

    private readonly _handleMultiSelectInstructionClick = (checked: boolean, specialInstructionId: number, instructionId: number): void => {
        const foundInstructionIndex = this._selectedInstructions.findIndex(x => x.specialInstructionId === specialInstructionId && x.commentId === instructionId);
        if (checked) {
            this._selectedInstructions.push({ specialInstructionId: specialInstructionId, commentId: instructionId });
        } else if (foundInstructionIndex !== -1) {
            this._selectedInstructions.splice(foundInstructionIndex, 1);
        }
    }

    private readonly _handleSingleSelectInstructionClick = (checked: boolean, specialInstructionId: number, instructionId: number): void => {
        const selectedInstructionIndex = this._selectedInstructions.findIndex(x => x.specialInstructionId === specialInstructionId);

        if (checked) {
            if (selectedInstructionIndex !== -1) {
                const prevId = this._selectedInstructions[selectedInstructionIndex]?.commentId;
                const prevInput = document.getElementById(`inp_customize_item_instruction_${specialInstructionId}_${prevId}`);
                prevInput?.setAttribute("aria-selected", "false"); // deselect previous
                Util.setElement("checked", `inp_customize_item_instruction_${specialInstructionId}_${prevId}`, false);
                this._selectedInstructions.splice(selectedInstructionIndex, 1);
            }
            const newInput = document.getElementById(`inp_customize_item_instruction_${specialInstructionId}_${instructionId}`);
            newInput?.setAttribute("aria-selected", "true"); // select new
            this._selectedInstructions.push({ specialInstructionId, commentId: instructionId });
        } else if (selectedInstructionIndex !== -1) {
            const input = document.getElementById(`inp_customize_item_instruction_${specialInstructionId}_${instructionId}`);
            input?.setAttribute("aria-selected", "false");
            this._selectedInstructions.splice(selectedInstructionIndex, 1);
        }
    }
}